/**
 * Numbers an experiment will take from the question.
 *
 * The router used to return exactly one thing - which experiment to run - and
 * every condition inside that experiment was written down in advance. So a
 * student who asked "what happens if I go to 40 mTorr?" got the same canned
 * span from 5 to 140 mTorr as a student who asked "what does pressure do?",
 * and the number they named was never used anywhere. The experiment was picked
 * from their question but not designed from it.
 *
 * A param is one knob an intent will accept from the question, carrying the
 * range it is allowed to take. Two things fill them in: the hosted model, which
 * reads the sentence, and `scanParams` below, which only reads the digits - so
 * a classroom with no network still gets the number the student typed.
 *
 * THE RANGE IS THE TRUST BOUNDARY, and it is the same one the intent id
 * already has. An id that is not in the catalogue is treated as no match; a
 * value outside its declared range is clamped to it. Nothing a model returns
 * reaches the solver unchecked, which is the entire reason this was safe to
 * widen. Declare each range as the knob's real physical limit and the clamp is
 * a physics guard as well as a trust one - there is then no second place where
 * "what is a sane pressure" is written down, and no way for the two to drift.
 *
 * What has NOT changed: the model still never produces a number of its own. It
 * may only carry across one the student already said. Everything the student
 * reads is still assembled from what the solver returned.
 */

export interface IntentParam {
  /** Key the intent reads off `ctx.params`. */
  id: string
  /** What the knob is, in a student's words. Goes to the model verbatim. */
  describe: string
  /** Unit symbol. Shown in labels, and matched by the offline scanner. */
  unit: string
  /** The knob's physical limits. Anything outside is clamped, not rejected. */
  min: number
  max: number
  /** Other spellings of the unit the offline scanner should accept. */
  aliases?: string[]
}

/** Knob values lifted out of a question, already clamped. */
export type IntentParams = Readonly<Record<string, number>>

/** Shared empty. Intents read `ctx.params.x` and get undefined, as intended. */
export const NO_PARAMS: IntentParams = Object.freeze({})

/**
 * Take whatever the model returned and keep only what the schema allows.
 *
 * Deliberately forgiving about shape and strict about range: a model that
 * answers `"40"` instead of `40` meant 40, and clamping catches the case that
 * actually matters. A key the schema does not declare is dropped entirely - an
 * intent can only be handed knobs it said it would accept.
 */
export function coerceParams(schema: IntentParam[] | undefined, raw: unknown): IntentParams {
  if (!schema?.length || !raw || typeof raw !== 'object') return NO_PARAMS
  const source = raw as Record<string, unknown>
  const out: Record<string, number> = {}
  for (const p of schema) {
    const v = source[p.id]
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN
    if (!Number.isFinite(n)) continue
    out[p.id] = Math.min(p.max, Math.max(p.min, n))
  }
  return Object.keys(out).length ? out : NO_PARAMS
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * The offline reader: a number followed by a unit the schema recognises.
 *
 * This is what keeps parameterised experiments from being a feature only
 * students with a network get. It is deliberately unable to read the sentence -
 * it finds "40 mTorr" and nothing else - and that limit is where the hosted
 * model earns the round trip it costs, rather than duplicating a regex.
 */
export function scanParams(schema: IntentParam[] | undefined, question: string): IntentParams {
  if (!schema?.length) return NO_PARAMS

  // Keyed by where the number sits in the question, so two knobs reaching for
  // the same digits are visible as a collision rather than resolved by order.
  const claims = new Map<number, Array<{ param: IntentParam; value: number }>>()

  for (const p of schema) {
    const units = [p.unit, ...(p.aliases ?? [])].filter(Boolean).map(escape)
    if (!units.length) continue
    const re = new RegExp(`(-?\\d+(?:\\.\\d+)?)\\s*(?:${units.join('|')})\\b`, 'gi')
    for (const m of question.matchAll(re)) {
      const value = Number(m[1])
      if (m.index === undefined || !Number.isFinite(value)) continue
      const at = claims.get(m.index)
      if (at) at.push({ param: p, value })
      else claims.set(m.index, [{ param: p, value }])
    }
  }

  const out: Record<string, number> = {}
  for (const claimants of claims.values()) {
    // Source power and bias power are both in watts, so both of them match
    // "600 W" and nothing in the digits says which was meant. Dropping the
    // ambiguous case is the honest reading - and it is exactly the case the
    // hosted router gets right, because it reads the words around the number.
    if (claimants.length !== 1) continue
    const { param, value } = claimants[0]
    out[param.id] = Math.min(param.max, Math.max(param.min, value))
  }
  return Object.keys(out).length ? out : NO_PARAMS
}
